import { ChangeEvent, useEffect, useState } from 'react';
import { DialogTitle } from '@headlessui/react';
import { useMutation } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../widgets/Modal';
import Input from '../../../widgets/Input';
import Button from '../../../widgets/Button';
import CopyButton from '../../../widgets/CopyButton/CopyButton';
import { useMfaModalContext } from '../../../../shared/providers/MfaModalProvider';

import { authenticate, verifyCode } from '../../../../shared/service/auth';
import {
  FACTORS,
  MfaModalState,
  VerifyToken,
} from '../../../../shared/constants/MFA';
import { addItemToLocalstorage } from '../../../../utilities/common/storage';
import { StorageItems } from '../../../../shared/constants/app';
import { validateCode } from '../../../../utilities/common/validateCode';

interface newQRCodeData {
  authSecret?: string;
  keyUri?: string;
  stateToken: string;
  mfaActive?: string;
  factorType?: string;
  isEnabled?: string;
  id?: string;
}

function QrCodeModal({ enabled, data }: { enabled: boolean; data: any }) {
  const [invalidAuthCode, setInvalidAuthCode] = useState<boolean>(false);
  const [invalidToken, setInvalidToken] = useState<boolean>(false);
  const [newQrCodeData, setNewQrCodeData] = useState<newQRCodeData | null>(
    null,
  );

  const {
    setActiveModal,
    setAccessToken,
    qrCodeData,
    setAllowAccess,
    setLogoutModal,
    tfaCode,
    setTfaCode,
  } = useMfaModalContext();

  const { t } = useTranslation();

  const placeholderText = t('SETUP_CODE_INPUT_SUBHEADER') || '';

  const [verifyCodeArgs, setVerifyCodeArgs] = useState({
    token: data.token,
    stateToken: data?.stateToken || '',
    authCode: tfaCode,
    factorType: FACTORS.TOTP,
  });

  useEffect(() => {
    setVerifyCodeArgs((prevVerifyCodeArgs) => ({
      ...prevVerifyCodeArgs,
      authCode: tfaCode,
    }));
  }, [tfaCode]);

  useEffect(() => {
    setNewQrCodeData(qrCodeData);
  }, [qrCodeData]);

  const { mutate: refetchMfaVerifyAuthCode, isPending: verifyCodeLoading } =
    useMutation({
      mutationKey: ['mfaVerifyAuthCode'],
      mutationFn: () => verifyCode(verifyCodeArgs),
      onSuccess: (response: any) => {
        if (response?.data?.accessToken) {
          if (newQrCodeData) {
            if ('keyUri' in newQrCodeData) {
              setAccessToken(response?.data?.accessToken);
              setActiveModal(MfaModalState.Enabled2Fa);
              addItemToLocalstorage(
                StorageItems.ACCESS_TOKEN,
                response?.data?.accessToken,
              );
              setAllowAccess({ login: false, layout: false });
            } else {
              setAccessToken(response?.data?.accessToken);
              setActiveModal(MfaModalState.NoModal);
              setAllowAccess({ login: true, layout: true });
            }
          }
        }
      },
      onError: (response: { data: any }) => {
        // Invalid authCode
        if (response?.data?.override?.code == VerifyToken.INVALID_AUTH_CODE) {
          setInvalidToken(false);
          setInvalidAuthCode(true);
        }
        // Invalid stateToken
        if (response?.data?.override?.code == VerifyToken.INVALID_STATE_TOKEN) {
          setInvalidToken(true);
          setTimeout(() => {
            refetchMfaActiveCheck();
          }, 100);
        }
      },
    });

  const { mutate: refetchMfaActiveCheck, isPending: authenticateLoading } =
    useMutation({
      mutationKey: ['checkMfaActive'],
      mutationFn: () => authenticate(data.token),
      onSuccess: (response: any) => {
        const totalFactors = response?.data?.factors;
        const primaryAuth = response?.data?.primaryAuth;
        if (totalFactors.length >= 2 && primaryAuth == null) {
          const authType = response?.data?.factors[0];
          const updatedVerifyCodeArgs = {
            ...verifyCodeArgs,
            stateToken: response?.data?.factors[0].stateToken || '',
          };
          setVerifyCodeArgs(updatedVerifyCodeArgs);
          if (authType == FACTORS.TOTP) {
            setNewQrCodeData(authType);
          }
        }
        if (primaryAuth !== null) {
          response?.data?.factors?.filter(async (item: any) => {
            if (item.id === primaryAuth) {
              const updatedVerifyCodeArgs = {
                ...verifyCodeArgs,
                stateToken: item.stateToken || '',
              };
              setVerifyCodeArgs(updatedVerifyCodeArgs);
              if (item.factorType == FACTORS.TOTP) {
                setNewQrCodeData(item);
              }
            }
          });
        } else {
          const authType = response?.data?.factors[0];

          const updatedVerifyCodeArgs = {
            ...verifyCodeArgs,
            stateToken: response?.data?.factors[0].stateToken || '',
          };
          setVerifyCodeArgs(updatedVerifyCodeArgs);
          if (authType.factorType == FACTORS.TOTP) {
            setNewQrCodeData(authType);
          }
        }
      },
    });

  const handleVerifyCode = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setInvalidAuthCode(false);
    refetchMfaVerifyAuthCode();
  };

  return (
    <Modal
      open={enabled}
      closeButton
      handleClose={() => {
        setLogoutModal(true);
      }}
      modalClassName="max-w-[500px] w-full px-[32px] py-[26px] pt-[20px] intl-2fa-modal"
    >
      <div>
        <DialogTitle
          as="h4"
          className="text-black-900 mb-2 text-lg font-semibold"
        >
          {t('SETUP_2FA_HEADER')}
        </DialogTitle>
        <span className="text-black-600 mb-[10px] text-xs">
          {t('SETUP_2FA_SUBHEADER')}
        </span>
      </div>
      <div className="grid text-center">
        <form
          className="mt-[10px] flex-col"
          onSubmit={(e: ChangeEvent<HTMLFormElement>) => {
            handleVerifyCode(e);
          }}
        >
          {newQrCodeData && (
            <>
              <QRCodeSVG
                value={newQrCodeData.keyUri || ''}
                size={200}
                className="mx-auto mt-10 mb-6"
              />

              <div className="relative m-auto mb-6 w-full max-w-[280px]">
                <input
                  type="text"
                  id="input-group-1"
                  className="bg-black-100 text-black-600 flex w-full gap-10 rounded-[9px] px-4 py-[16px] pr-12 text-sm text-ellipsis focus:outline-hidden"
                  disabled={true}
                  value={newQrCodeData.authSecret || ''}
                />
                <div className="absolute inset-y-0 right-4 flex cursor-pointer items-center pl-3">
                  <CopyButton
                    valueToBeCopied={newQrCodeData.authSecret || ''}
                    showText={false}
                  />
                </div>
              </div>
            </>
          )}
          <span className="intl-remove-br text-black-600 mb-20 block px-[45px] text-sm">
            {t('SETUP_COPY_CODE')}
          </span>
          <div className="mt-5 mb-6 grid text-left">
            <label
              htmlFor="tfaCode"
              className="text-black-700 mb-[6px] text-sm font-semibold"
            >
              {t('SETUP_CODE_INPUT_HEADER')}
            </label>
            <Input
              name="tfaCode"
              id="tfaCode"
              placeholder={placeholderText}
              autoComplete="off"
              className={` ${invalidAuthCode && 'border-redd-600'}`}
              value={tfaCode}
              onChange={(e) => setTfaCode(validateCode(e, 6))}
            />

            {invalidAuthCode && (
              <p className="text-redd-600 mt-1 text-sm leading-6">
                {t('MFA_INVALID_CODE')}
              </p>
            )}
            {invalidToken && (
              <p className="text-redd-600 mt-1 text-sm leading-6">
                {t('SESSION_EXPIRED_2FA')}
              </p>
            )}
          </div>
          <Button
            type="submit"
            size="lg"
            disabled={!tfaCode}
            isLoading={verifyCodeLoading || authenticateLoading}
          >
            {t('AUTHENTICATE')}
          </Button>
        </form>
      </div>
    </Modal>
  );
}

export default QrCodeModal;
